import React from "react";
import { FunnelPair, FunnelStep, PathKey, PlatformKey } from "./types";
import { fmtN } from "./format";
import { Cell, Fx, Row, Sheet, endToEnd, keptAt, leakAt, leakSentence, lostAt, stepLabel } from "./Sheet";

// The "Talent drop-off" and "Fans" sheets — one component, one funnel path.
// Website | App | All side by side (no platform toggle to remember): read
// across to compare, read down to find the leak. Only the All group carries
// Kept % and Last week, so the sheet stays readable at a glance.

const PLATFORMS: PlatformKey[] = ["web", "app", "all"];

export function FunnelSheet({
  path,
  funnels,
  onShowNeedsBank,
}: {
  path: PathKey;
  funnels: Record<PlatformKey, FunnelPair>;
  onShowNeedsBank?: () => void;
}) {
  const cols = PLATFORMS.map((p) => funnels[p][path]);
  const [web, app, all] = cols;
  const leaks = cols.map(leakAt);
  const anyCombined = cols.some((steps) => steps.some((s) => s.combined));
  const noun = path === "dj" ? "talent" : "fan";

  const people = (s: FunnelStep): Cell => ({ v: `${fmtN(s.n)}${s.combined ? "†" : ""}`, cls: "gr-sh-num" });
  const lost = (steps: FunnelStep[], i: number, leak: number): Cell => {
    const l = lostAt(steps, i);
    if (l == null) return { v: "—", cls: "gr-sh-num gr-sh-none" };
    return { v: l ? `−${fmtN(l)}` : "0", cls: `gr-sh-num${leak === i ? " gr-sh-leak" : ""}`, title: leak === i ? "Most people lost here" : undefined };
  };

  const rows: Row[] = all.map((s, i) => {
    const kept = keptAt(all, i);
    const link =
      onShowNeedsBank && s.key === "DJ_activated" ? (
        <>
          {stepLabel(s, path)}{" "}
          <button type="button" className="gr-link" onClick={onShowNeedsBank}>
            see who's stuck →
          </button>
        </>
      ) : (
        stepLabel(s, path)
      );
    return {
      cls: i === 0 ? "gr-sh-ctx" : undefined,
      cells: [
        { v: link, cls: "gr-sh-step" },
        people(web[i]),
        lost(web, i, leaks[0]),
        people(app[i]),
        lost(app, i, leaks[1]),
        people(s),
        lost(all, i, leaks[2]),
        { v: kept == null ? "—" : `${kept}%`, cls: `gr-sh-num${kept == null ? " gr-sh-none" : ""}` },
        { v: fmtN(s.prev), cls: "gr-sh-num gr-sh-prev" },
      ],
    };
  });

  const e2e = cols.map((steps) => endToEnd(steps));
  const e2ePrev = endToEnd(all, "prev");
  const pct = (v: number | null) => (v == null ? "—" : `${v}%`);
  rows.push({
    cls: "gr-sh-total",
    cells: [
      { v: `Chose ${noun} → ${stepLabel(all[all.length - 1], path).toLowerCase()}`, cls: "gr-sh-step" },
      { v: pct(e2e[0]), span: 2, cls: "gr-sh-num" },
      { v: pct(e2e[1]), span: 2, cls: "gr-sh-num" },
      { v: pct(e2e[2]), span: 3, cls: "gr-sh-num" },
      { v: pct(e2ePrev), cls: "gr-sh-num gr-sh-prev" },
    ],
  });

  const head: Row[] = [
    {
      cls: "gr-sh-grp",
      cells: [{ v: "" }, { v: "Website", span: 2 }, { v: "App", span: 2 }, { v: "All", span: 4 }],
    },
    {
      cls: "gr-sh-head",
      cells: [
        { v: "Step", cls: "gr-sh-step" },
        { v: "People" },
        { v: "Lost" },
        { v: "People" },
        { v: "Lost" },
        { v: "People" },
        { v: "Lost" },
        { v: "Kept" },
        { v: "Last week", cls: "gr-sh-prev" },
      ],
    },
  ];

  const main = leakSentence(all, path);
  const sides = [
    ["On the website", leakSentence(web, path)],
    ["In the app", leakSentence(app, path)],
  ].filter(([, s]) => s);

  return (
    <>
      <Fx>
        {main ? (
          <>
            Biggest leak: <b>{main}</b>.
            {sides.map(([where, s]) => (
              <span key={where}>
                {" "}
                {where}: {s}.
              </span>
            ))}
          </>
        ) : (
          `No ${noun} leak to point at in this window yet.`
        )}
      </Fx>
      <Sheet cols={9} head={head} rows={rows} label={`${noun} drop-off`} />
      <div className="gr-note">
        Yellow = where the most people were lost. "Last week" = the same-length window just before this one.
        {anyCombined ? " † web + app combined — can't be compared with the row above it." : ""} Website and App columns are floors: a
        signup or activation whose device header was missing counts only in All.
      </div>
    </>
  );
}
